import React from "react";
import { Provider } from "react-redux";
import { applyMiddleware, compose, createStore } from "redux";
import thunk from "redux-thunk";
import { throttle } from "underscore";
import { datadogLogs } from "@datadog/browser-logs";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { withLDProvider } from "launchdarkly-react-client-sdk";
import { QueryClient, QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import Avvir from "avvir";

import Application from "./routers/application";
import eventTrackingMiddleware from "../services/event_tracking_services/event_tracking_middleware";
import pageViewTrackingMiddleware from "../services/event_tracking_services/page_view_tracking_middleware";
import pageTitleMiddleware from "../services/middleware/page_title_middleware";
import rootReducer from "../services/reducers/root_reducer";
import signOutUser from "../actions/sign_in_page/sign_out_user";
import theme from "../style/mui_theme";
import undoMiddleware from "../services/middleware/undo_middleware";
import elementCacheMiddleware from "../services/middleware/element_cache_middleware";
import UtilityContext from "./utility_context";
import { CUSTOM_FAILURE_MESSAGE_DISPLAYED } from "../events/notifications/failure_notifications/custom_failure_message_displayed";
import { getSelectionToSave } from "../services/reducers/reduce_selection";
import { readStore, writeStore } from "../services/utilities/store_in_local_storage";
import { routesEnhancer, routesMiddleware } from "../router_redux_config";

// BEGIN temporary debugging
const observer = new MutationObserver((list, observer) => {
  for (const event of list) {
    const newValue = (event.target as HTMLHtmlElement).attributes["lang"].value;
    throw new Error(`Language changed from "${event.oldValue}" to "${newValue}"`);
  }
});
observer.observe(document.documentElement, {attributes:true, attributeFilter:["lang"], attributeOldValue:true});
// END temporary debugging

const AVVIR_ENV = process.env.REACT_APP_AVVIR_ENVIRONMENT;
console.info(`Avvir Environment: ${AVVIR_ENV}`);
Avvir.api.config.setConfigurationForEnvironment(AVVIR_ENV);
Avvir.api.config.getConfiguration().AVVIR_GATEWAY_URL = process.env.REACT_APP_WEB_API_GATEWAY_DOMAIN;

datadogLogs.init({
  clientToken: process.env.REACT_APP_DATADOG_API,
  site: "datadoghq.com",
  forwardErrorsToLogs: true,
  sampleRate: 100,
  service: "avvir-web",
  env: `node_${process.env.NODE_ENV}:gateway_${AVVIR_ENV}`,
  //@ts-ignore
  forwardConsoleLogs: ["warn", "error"]
});

const composeEnhancers = window["__REDUX_DEVTOOLS_EXTENSION_COMPOSE__"]
                         ? window["__REDUX_DEVTOOLS_EXTENSION_COMPOSE__"]({
    actionSanitizer: (event) => {
      if (event?.payload?.mesh) {
        return {
          ...event,
          payload: {
            ...event.payload,
            mesh: "<<THREE.js Mesh Object>>"
          }
        };
      } else {
        return event;
      }
    }
  })
                         : compose;

function getExpireTime(state): number {
  const hxAuthExpireTime = state.user?.hxAuthUser?.expireTime;
  if (hxAuthExpireTime != null) {
    return hxAuthExpireTime;
  }

  const legacyExpireTime = state.user?.account?.expireTime;
  if (legacyExpireTime != null) {
    return legacyExpireTime;
  }

  return Infinity;
}

const expiredJwtTokenMiddleware = store => next => action => {
  const state = store.getState();
  const expTime = getExpireTime(state);
  if (expTime < Infinity) {
    const now = Date.now();
    const expired = now > expTime * 1000;
    if (expired) {
      return signOutUser()(next).then(() => {
        return next({
          type: CUSTOM_FAILURE_MESSAGE_DISPLAYED,
          payload: {
            message: "Login Expired: Please login again"
          }
        });
      });
    }
  }
  return next(action);
};

const store = createStore(rootReducer, readStore(), composeEnhancers(
  routesEnhancer,
  applyMiddleware(expiredJwtTokenMiddleware, thunk, routesMiddleware, pageViewTrackingMiddleware, eventTrackingMiddleware, undoMiddleware, pageTitleMiddleware, elementCacheMiddleware)
));

store.subscribe(throttle(() => {
  writeStore({
    selection: getSelectionToSave(store.getState().selection)
  });
}, 1000)); // only try to serialize the store at most once per second

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // globally default to never refetch data;
      staleTime: Infinity,
      // globally default to not retry failed queries
      retry: false
    }
  }
});

const AvvirApp = () => {
  return <MuiThemeProvider theme={theme}>
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <UtilityContext/>
          <Application/>
          <ReactQueryDevtools initialIsOpen={false}/>
        </QueryClientProvider>
      </Provider>
    </LocalizationProvider>
  </MuiThemeProvider>;
};

export default React.memo(withLDProvider({
  clientSideID: process.env.REACT_APP_LD_CLIENT_SIDE_ID,
  user: {
    key: "anonymous",
    anonymous: true
  }
})(AvvirApp));
